import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRestApi } from "../../useClient";
import { useGetIntegrationsQuery } from "../integrations";
import { customFieldsApi, } from "../customFields";
// Hook to manage custom fields for an integration
export function useCustomFields(integrationId) {
    const api = useRestApi();
    const queryClient = useQueryClient();
    // Query to get all field definitions
    const { data: fieldDefinitions = [], isLoading, error, refetch, } = useQuery({
        queryKey: ["customFields", integrationId],
        queryFn: () => {
            if (!integrationId)
                throw new Error("Integration ID is required");
            console.log("Fetching custom fields for integration:", integrationId);
            return customFieldsApi.getFieldDefinitions(api, integrationId);
        },
        enabled: !!integrationId,
    });
    // Debug logging
    console.log("Custom Fields Hook Debug:", {
        integrationId,
        fieldDefinitions,
        isLoading,
        error,
        enabled: !!integrationId,
    });
    // Mutation to create field definition
    const createFieldMutation = useMutation({
        mutationFn: ({ integrationId, fieldData, }) => customFieldsApi.createFieldDefinition(api, integrationId, fieldData),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["customFields", integrationId],
            });
        },
    });
    // Mutation to update field definition
    const updateFieldMutation = useMutation({
        mutationFn: ({ integrationId, fieldId, fieldData, }) => customFieldsApi.updateFieldDefinition(api, integrationId, fieldId, fieldData),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["customFields", integrationId],
            });
        },
    });
    // Mutation to delete field definition
    const deleteFieldMutation = useMutation({
        mutationFn: ({ integrationId, fieldId, }) => customFieldsApi.deleteFieldDefinition(api, integrationId, fieldId),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["customFields", integrationId],
            });
        },
    });
    // Mutation to reorder field definitions
    const reorderFieldsMutation = useMutation({
        mutationFn: ({ integrationId, appliesTo, fieldOrders, }) => customFieldsApi.reorderFieldDefinitions(api, integrationId, appliesTo, fieldOrders),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["customFields", integrationId],
            });
        },
    });
    return {
        fieldDefinitions,
        isLoading,
        error,
        refetch,
        createField: (fieldData) => {
            if (!integrationId)
                throw new Error("Integration ID is required");
            return createFieldMutation.mutateAsync({ integrationId, fieldData });
        },
        updateField: (fieldId, fieldData) => {
            if (!integrationId)
                throw new Error("Integration ID is required");
            return updateFieldMutation.mutateAsync({
                integrationId,
                fieldId,
                fieldData,
            });
        },
        deleteField: (fieldId) => {
            if (!integrationId)
                throw new Error("Integration ID is required");
            return deleteFieldMutation.mutateAsync({ integrationId, fieldId });
        },
        isCreating: createFieldMutation.isPending,
        isUpdating: updateFieldMutation.isPending,
        isDeleting: deleteFieldMutation.isPending,
        reorderFields: (appliesTo, fieldOrders) => {
            if (!integrationId)
                throw new Error("Integration ID is required");
            return reorderFieldsMutation.mutateAsync({
                integrationId,
                appliesTo,
                fieldOrders,
            });
        },
        isReordering: reorderFieldsMutation.isPending,
    };
}
// Hook to get available integrations
export function useIntegrations() {
    const [result] = useGetIntegrationsQuery();
    const integrations = result.data?.getIntegrations?.integrations?.map((integration) => ({
        id: integration.id,
        name: integration.displayName,
        customerId: integration.customerId,
        integrationType: integration.integrationType,
        integrationSource: integration.integrationSource,
    })) || [];
    return {
        integrations,
        isLoading: result.fetching,
        error: result.error,
    };
}
